import React, {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';

import { useDropdownPortal } from '../../hooks/useDropdownPortal';

import ChevronDown from '../Icons/Chevron/ChevronDown';

/**
 * setup types for use with the component
 */
type EnumLike = Record<string, string | number>;
type EnumValue<E extends EnumLike> = E[keyof E];

/**
 * Converts a camelCase, PascalCase, or SCREAMING_SNAKE_CASE string
 * into a more human-readable format by:
 * - Replacing underscores with spaces
 * - Inserting spaces before capital letters or transitions
 *
 * Examples:
 * - "CreatedAt" → "Created At"
 * - "HTTPServerError" → "HTTP Server Error"
 * - "NET_NEW" → "NET NEW"
 *
 * @param k - The enum key or identifier string to format.
 * @returns A human-friendly label version of the input key.
 */
const humanizeKey = (k: string) =>
    k
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim();

/**
 * Defines the public methods exposed by the EnumSelectDropdown via `ref`.
 * Currently supports:
 * - `clear`: Clears the selected enum value and resets internal state.
 * - `reset`: Restores the `defaultValue` (or clears when none was given), so a
 *   "reset filters" action can return to the page-load state rather than empty.
 */
export type EnumSelectDropdownHandle = {
    clear: () => void;
    reset: () => void;
};

/**
 * Returns the string keys of an enum-like object, filtering out numeric keys.
 *
 * Useful for working with TypeScript enums which include reverse mappings
 * (i.e., numeric-to-string keys) when compiled.
 *
 * @param e - The enum-like object to extract keys from.
 * @returns An array of string keys (e.g., ['Pending', 'Complete']).
 */
const getEnumKeys = (e: EnumLike) => Object.keys(e).filter(k => isNaN(Number(k)));

/**
 * Determines whether the given enum-like object includes numeric keys,
 * indicating that it’s a numeric TypeScript enum (with reverse mapping).
 *
 * @param e - The enum-like object to inspect.
 * @returns `true` if the enum is numeric; `false` otherwise.
 */
const isNumericEnum = (e: EnumLike) => Object.keys(e).some(k => !isNaN(Number(k)));

type EnumSelectDropdownProps<E extends EnumLike> = {
    enumObj: E;
    defaultValue?: EnumValue<E>;
    onChange?: (v: EnumValue<E> | undefined) => void; // undefined = cleared
    label?: string;
    placeholder?: string;           // e.g. 'Select...'
    clearable?: boolean;            // default true
    size?: 'sm' | 'lg';
    disabled?: boolean;
    wrapperClassName?: string;
    triggerClassName?: string;
    buttonWidthCh?: number;         // default 22
    labels?: Record<string, string>;
    disabledKeys?: Array<keyof E & string>;        // e.g. ['Complete']
    disabledValues?: Array<EnumValue<E>>;          // e.g. [FormStatus.Complete]
    disableIf?: (opt: { key: string; val: EnumValue<E>; label: string }) => boolean; // custom rule
};

/** Generic component type helper so forwardRef plays nice with <E> */
type EnumSelectComponent = (<E extends EnumLike>(
    props: EnumSelectDropdownProps<E> & { ref?: React.Ref<EnumSelectDropdownHandle> }
) => React.ReactElement | null) & {
    displayName?: string;
};

export const EnumSelectDropdown = forwardRef(function EnumSelectDropdownInner<E extends EnumLike>(
    {
        enumObj,
        defaultValue,
        onChange,
        label,
        placeholder = 'Select...',
        clearable = true,
        size,
        disabled,
        wrapperClassName,
        triggerClassName,
        buttonWidthCh,
        labels = {},
        disabledKeys,
        disabledValues,
        disableIf,

    }: EnumSelectDropdownProps<E>,
    ref: React.Ref<EnumSelectDropdownHandle>
) {
    const keys = useMemo(() => getEnumKeys(enumObj), [enumObj]);
    const numeric = useMemo(() => isNumericEnum(enumObj), [enumObj]);
    const options = useMemo(
        () =>
            keys.map(k => {
                const val = enumObj[k as keyof E] as EnumValue<E>;
                const labelText = labels[k] ?? humanizeKey(k);
                return { key: k, val, label: labelText };
            }),
        [keys, enumObj, labels]
    );

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<EnumValue<E> | undefined>(defaultValue ?? undefined);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const sizeBtn = size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';

    const { portal } = useDropdownPortal(containerRef, open, setOpen);

    /**
     * Exposes the `clear()` and `reset()` methods to the parent via ref,
     * allowing it to programmatically reset the dropdown.
     *
     * - `clear` empties the selection and triggers `onChange` with undefined
     * - `reset` restores `defaultValue` instead, behaving like `clear` when none was given
     * - Both close the dropdown
     */
    useImperativeHandle(ref, () => ({
        clear: () => {
            setSelected(undefined);
            onChange?.(undefined);
            setOpen(false);
        },
        reset: () => {
            setSelected(defaultValue ?? undefined);
            onChange?.(defaultValue ?? undefined);
            setOpen(false);
        }
    }), [onChange, defaultValue]);

    /**
     * Memoized Set of disabled enum keys, for fast lookup by name (key).
     * These correspond to the keys of the enum (e.g., 'Archived').
     */
    const disabledKeySet = useMemo(() => new Set(disabledKeys ?? []), [disabledKeys]);

    /**
     * Memoized Set of disabled enum values, for fast lookup by value.
     * Values are stringified to ensure consistent comparison.
     */
    const disabledValSet = useMemo(
        () => new Set((disabledValues ?? []).map(v => String(v))),
        [disabledValues]
    );

    /**
     * Determines whether an option is disabled.
     *
     * Checks against:
     * - Disabled keys (by enum key)
     * - Disabled values (by enum value)
     * - Optional custom `disableIf()` rule
     *
     * @param o - The option object (key, value, label)
     * @returns `true` if the option should be disabled
     */
    const isDisabled = (o: { key: string; val: EnumValue<E>; label: string }) =>
        disabledKeySet.has(o.key) ||
        disabledValSet.has(String(o.val)) ||
        !!disableIf?.(o);

    /**
     * Memoized display label for the currently selected value.
     *
     * Finds the matching option from the list and returns its label.
     * Falls back to the raw value string if no label is found.
     */
    const display = useMemo(() => {
        if (selected === undefined) return '';
        const found = options.find(o => String(o.val) === String(selected));
        return found?.label ?? String(selected);
    }, [selected, options]);

    /**
     * Handles selection of an option from the dropdown.
     *
     * - Finds the matching option by value
     * - Verifies it's not disabled
     * - Sets the new value and emits the change
     *
     * @param raw - The raw stringified value from the clicked option
     */
    const choose = (raw: string) => {
        const opt = options.find(o => String(o.val) === raw);
        if (!opt || isDisabled(opt)) return; // safety
        const next = numeric ? (Number(raw) as unknown as EnumValue<E>) : (raw as unknown as EnumValue<E>);
        setSelected(next);
        onChange?.(next);
        setOpen(false);
    };

    /**
     * Clears the currently selected value.
     *
     * - Resets `selected` state to undefined
     * - Triggers `onChange` with undefined
     * - Closes the dropdown
     */
    const clear = () => {
        setSelected(undefined);
        onChange?.(undefined);
        setOpen(false);
    };

    return (
        <div
            className={['position-relative', wrapperClassName].filter(Boolean).join(' ')}
            ref={containerRef}
        >
            {label && <label className='form-label fw-semibold d-block mb-1'>{label}</label>}

            <button
                type='button'
                className={[
                    'btn',
                    'btn-outline-secondary',
                    'btn-form-outline',
                    'btn-form-focus',
                    'd-flex',
                    'align-items-center',
                    'justify-content-between',
                    'gap-2',
                    'text-truncate',
                    sizeBtn,
                    triggerClassName
                ].filter(Boolean).join(' ')}
                style={{
                    width: '100%',
                    ...(buttonWidthCh ? { maxWidth:`${buttonWidthCh}ch` } : {})
                }}
                onClick={() => !disabled && setOpen(v => !v)}
                aria-expanded={open}
                aria-haspopup='listbox'
                disabled={disabled}
            >
                <span className='flex-grow-1 text-truncate text-start min-w-0'>
                    {display || placeholder}
                </span>
                <ChevronDown />
            </button>

            {portal(
                <div
                    className='dropdown-menu show p-0 shadow enum-menu'
                    role='listbox'
                    style={{
                        width: '100%',
                        ...(buttonWidthCh ? { maxWidth:`${buttonWidthCh}ch` } : {})
                    }}
                >
                    {clearable && (
                        <>
                            <button
                                type='button'
                                className='dropdown-item text-muted'
                                onClick={clear}
                            >
                                {placeholder}
                            </button>
                            <div className='dropdown-divider' />
                        </>
                    )}
                    {options.map(o => {
                        const dis = isDisabled(o);
                        const active = String(o.val) === String(selected);
                        return (
                            <button
                                key={o.label}
                                type='button'
                                className={`dropdown-item${active ? ' active' : ''}${dis ? ' disabled text-muted' : ''}`}
                                onClick={() => { if (!dis) choose(String(o.val)); }}
                                disabled={dis}
                            >
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            )}

        </div>
    );
}) as EnumSelectComponent;

EnumSelectDropdown.displayName = "EnumSelectDropdown";

export default EnumSelectDropdown;
